import moment from 'moment';
import { descend, flatten, pathOr, prop, sort } from 'ramda';
import {
  DriverRequests,
  ExceptionsAndOpportunity,
  StopType,
  LoadTime,
} from '../enums/FilterTypes';
import { prepareDriver } from './driver';
import { defaultOrderFn } from '../features/routeDetail/model';
import { isEmptyOdfDriver } from '../features/driverDetails/model';

export const FilterTypes = {
  routeStatus: 'routeStatus',
  exceptionsAndOpportunity: 'exceptionsAndOpportunity',
  stopType: 'stopType',
  driverRequests: 'driverRequests',
  loadTime: 'loadTime',
};

const includesExceptionsAndOpportunity = (filters, route) =>
  filters.some((type) => {
    switch (type) {
      case ExceptionsAndOpportunity.Postcode:
        return route.postcodeExceptions > 0;
      case ExceptionsAndOpportunity.Duty:
        return route.dutyTimeExceptions > 0 || route.legalDutyExceptions > 0;
      case ExceptionsAndOpportunity.Service:
        return route.serviceExceptions > 0;
      case ExceptionsAndOpportunity.Opportunity:
        return route.opportunityTime > 0;
      default:
        return false;
    }
  });

const includesStopType = (filters, route) =>
  filters.some((type) => {
    switch (type) {
      case StopType.TenThirty:
        return route.stopStats.totalBy1030 > 0;
      case StopType.Twelve:
        return route.stopStats.totalBy12 > 0;
      case StopType.Evening:
        return route.stopStats.totalEvening > 0;
      default:
        return false;
    }
  });

const includesDriverRequests = (filters, route) =>
  filters.some((type) => {
    if (type === DriverRequests.Requests) {
      return route.totalOpenRequests > 0;
    }

    return false;
  });

const includesLoadTime = (filters, route) =>
  filters.some((type) => {
    switch (type) {
      case LoadTime.OnSchedule:
        return (
          route.firstParcelScanTime != null &&
          !route.lowLoadRate &&
          !route.exceededLoadTime
        );
      case LoadTime.BehindSchedule:
        return route.lowLoadRate;
      case LoadTime.ScanTimeExceeded:
        return route.exceededLoadTime;
      default:
        return false;
    }
  });

export const filterRoutes = (filters, routes = []) =>
  routes.filter((route) => {
    if (
      filters[FilterTypes.routeStatus] &&
      !filters[FilterTypes.routeStatus].includes(route.routeStatus)
    ) {
      return false;
    }

    if (
      filters[FilterTypes.exceptionsAndOpportunity] &&
      !includesExceptionsAndOpportunity(
        filters[FilterTypes.exceptionsAndOpportunity],
        route
      )
    ) {
      return false;
    }

    if (
      filters[FilterTypes.stopType] &&
      !includesStopType(filters[FilterTypes.stopType], route)
    ) {
      return false;
    }

    if (
      filters[FilterTypes.driverRequests] &&
      !includesDriverRequests(filters[FilterTypes.driverRequests], route)
    ) {
      return false;
    }

    return !(
      filters[FilterTypes.loadTime] &&
      !includesLoadTime(filters[FilterTypes.loadTime], route)
    );
  });

export const updateStopInStopList = (stops, updatedStop) => {
  const updatedStopIndex = stops.findIndex(
    (s) => s.stopNumber === updatedStop.stopNumber
  );
  const updatedStops = stops.slice();
  updatedStops[updatedStopIndex] = updatedStop;
  return updatedStops;
};
export const removeStopFromList = (stops, { stopNumber }) =>
  stops.filter((stop) => stop.stopNumber !== stopNumber);

export const processRequestStopInList = (stops, stop, requests) => {
  const newParcels = stop?.parcel.filter(
    ({ parcelCode }) =>
      !requests.find((request) => request.parcelCode === parcelCode)
  );

  if (newParcels.length) {
    return updateStopInStopList(stops, { ...stop, parcel: newParcels });
  } else {
    return removeStopFromList(stops, stop);
  }
};

export const prepareRoute = (formValues, multiRunRoute = false) => ({
  multiRunRoute,
  date: moment.utc().toDate().getTime(),
  driver: prepareDriver(formValues),
  routeCode: formValues.routeNumber,
  sectionCode: formValues.sectionCode || null,
  odfRouteOwner: !isEmptyOdfDriver(formValues.odfDriver)
    ? formValues.odfDriver.driverCode
    : null,
});

export const sortRoutesBySectionRoute = (routes) => {
  const bySectionRoute = descend(prop('sectionRoute'));
  return sort(bySectionRoute, routes);
};

export const serializeDateObject = (obj) => {
  const updated = { ...obj };

  for (const key in updated) {
    if (updated[key] instanceof Date) {
      updated[key] = updated[key].toISOString();
    }
  }
  return updated;
};

export const deserializeDateObject = (obj, dateKeys = []) => {
  const updated = { ...obj };

  for (const key of dateKeys) {
    if (typeof updated[key] === 'string') {
      updated[key] = new Date(updated[key]);
    }
  }
  return updated;
};

export const prepareNoStopNumberRouteStops = (stops) => {
  const sortedStops = stops.sort((previous, next) =>
    defaultOrderFn(previous, next, 'sequenceNumber')
  );

  const originalStopsData = [];
  const updatedStops = sortedStops.map((stop, i) => {
    const stopNumber = `${i + 1}`;
    originalStopsData.push({
      stopId: stop.stopId,
      stopNumber,
    });
    return {
      ...stop,
      displayNumber: stopNumber,
      stopNumber,
      sequenceNumber: stopNumber,
    };
  });

  return { originalStopsData, updatedStops };
};

const getAvailableStopNumbers = (stopNumbers) =>
  [...Array(999).keys()].filter((el) => el && !stopNumbers.includes(el));

export const updateStopsWithValidStopNumber = (stops) => {
  const sortedStops = stops.sort((previous, next) =>
    defaultOrderFn(previous, next, 'sequenceNumber')
  );
  let availableStopNumbers = getAvailableStopNumbers(
    sortedStops.map((stop) => stop.stopNumber)
  );
  let originalStopsData = [];
  const updatedStops = sortedStops.map((stop) => {
    if (!stopWithValidStopNumber(stop)) {
      const stopNumber = availableStopNumbers.shift();
      if (stopNumber) {
        originalStopsData.push({
          stopId: stop.stopId,
          stopNumber: `${stopNumber}`,
        });
        return {
          ...stop,
          displayNumber: `${stopNumber}`,
          stopNumber,
          sequenceNumber: stopNumber,
        };
      }
    }

    originalStopsData.push({
      stopId: stop.stopId,
      stopNumber: `${stop.stopNumber}`,
    });
    return stop;
  });

  return { originalStopsData, updatedStops };
};

export const stopWithValidStopNumber = (stop) => {
  const MAX_STOP_NUMBER = 999;
  const MAX_DISPLAY_NUMBER_LENGTH = 10;
  return (
    stop.stopNumber &&
    +stop.stopNumber <= MAX_STOP_NUMBER &&
    stop.displayNumber?.length <= MAX_DISPLAY_NUMBER_LENGTH
  );
};

export const hasInvalidStopNumbers = (stops) =>
  stops.some((stop) => !stopWithValidStopNumber(stop));

export const routeParcels = (route) =>
  flatten(route.stop.map(({ parcel }) => parcel));

export const getSuitableStopsForReConsolidation = (stops) => {
  const grouped = stops
    .filter(
      (stop) =>
        stop.parcel &&
        stop.parcel.length &&
        stop.parcel.every(
          (parcel) =>
            parcel.deliveryId &&
            parcel.deliveryId.trim() !== '' &&
            parcel.deliveryId === stop.parcel[0].deliveryId
        )
    )
    .reduce((grouped, stop) => {
      const deliveryId = stop.parcel[0].deliveryId;
      const key = deliveryId;

      if (!grouped[key]) {
        grouped[key] = [];
      }
      grouped[key].push(stop);

      return grouped;
    }, {});

  // Filter out delivery groups with only one stop
  return Object.fromEntries(
    Object.entries(grouped).filter(([, stops]) => stops.length > 1)
  );
};

export const hasRouteRequests = (route) =>
  pathOr([], ['request'], route).length > 0;
